import type {
  ObjectWorkspaceAnchor,
  ObjectWorkspaceObject,
  ObjectWorkspaceShellProps,
} from '@heidi/agents';
import { setOpenArtifactAtom } from '@heidi/agents/data';
import type { QueryPatientDocument } from '@heidi/patients/contracts';
import { useQueryPatientDocuments, useQueryPatientRecap } from '@heidi/patients/data';
import { ClinicalTaskStatus } from '@heidi/schema/ml-scribe';
import { useQueryDocumentsBySessionId } from '@heidi/session/data';
import { useQueryClinicalTasks } from '@heidi/tasks/data';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { navV3Messages } from '../nav-v3.messages';
import {
  resolvePatientDocumentsState,
  resolveSessionDocumentsState,
} from './object-workspace-documents';
import { resolveObjectWorkspaceTasksState } from './object-workspace-tasks';

// Same cap as Work Home so completed tasks cannot fill the page.
const OPEN_TASKS_PAGE_LIMIT = 100;

export interface ObjectWorkspaceDocumentPreview {
  patientProfileId: string;
  document: QueryPatientDocument;
}

/** Next Steps, Documents and the read-only summary for the open patient or visit. */
export function useObjectWorkspaceFacetData(object: ObjectWorkspaceObject | null): Pick<
  ObjectWorkspaceShellProps,
  'tasksState' | 'documentsState' | 'summary' | 'onDocumentSelect'
> & {
  documentPreview: ObjectWorkspaceDocumentPreview | null;
  closeDocumentPreview: () => void;
} {
  const intl = useIntl();
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);
  const sessionId = object?.kind === 'visit' ? object.id : '';
  const patientProfileId =
    object?.kind === 'patient' ? (object.patientProfileId ?? '') : '';

  const tasksEnabled = Boolean(sessionId || patientProfileId);
  const openTaskQuery = {
    ...(sessionId ? { sessionId } : { patientProfileId }),
    sortBy: 'created_at',
    sortOrder: 'desc' as const,
    limit: OPEN_TASKS_PAGE_LIMIT,
  };
  const todoTasks = useQueryClinicalTasks(
    { ...openTaskQuery, status: ClinicalTaskStatus.todo },
    { enabled: tasksEnabled }
  );
  const inProgressTasks = useQueryClinicalTasks(
    { ...openTaskQuery, status: ClinicalTaskStatus.in_progress },
    { enabled: tasksEnabled }
  );
  const sessionDocuments = useQueryDocumentsBySessionId(sessionId);
  const patientDocuments = useQueryPatientDocuments(patientProfileId);
  const { data: recap } = useQueryPatientRecap(patientProfileId);
  const [documentPreview, setDocumentPreview] =
    useState<ObjectWorkspaceDocumentPreview | null>(null);

  const tasksUpdatedAt = Math.max(todoTasks.dataUpdatedAt, inProgressTasks.dataUpdatedAt);
  const tasksState = useMemo(
    () =>
      resolveObjectWorkspaceTasksState({
        kind: object?.kind ?? 'visit',
        hasScope: tasksEnabled,
        tasks: [...(todoTasks.data?.tasks ?? []), ...(inProgressTasks.data?.tasks ?? [])],
        isLoading: todoTasks.isLoading || inProgressTasks.isLoading,
        isError: todoTasks.isError || inProgressTasks.isError,
        now: new Date(tasksUpdatedAt || Date.now()),
      }),
    [
      inProgressTasks.data,
      inProgressTasks.isError,
      inProgressTasks.isLoading,
      object?.kind,
      tasksEnabled,
      tasksUpdatedAt,
      todoTasks.data,
      todoTasks.isError,
      todoTasks.isLoading,
    ]
  );

  const untitledTitle = intl.formatMessage(navV3Messages.untitledDocument);
  const documentsState = useMemo(() => {
    if (sessionId) {
      return resolveSessionDocumentsState({
        documents: sessionDocuments.data?.documents,
        isLoading: sessionDocuments.isLoading,
        isError: sessionDocuments.isError,
        untitledTitle,
      });
    }
    if (!patientProfileId) return { status: 'ready' as const, documents: [] };
    return resolvePatientDocumentsState({
      documents: patientDocuments.data,
      isLoading: patientDocuments.isLoading,
      isError: patientDocuments.isError,
    });
  }, [
    patientDocuments.data,
    patientDocuments.isError,
    patientDocuments.isLoading,
    patientProfileId,
    sessionDocuments.data,
    sessionDocuments.isError,
    sessionDocuments.isLoading,
    sessionId,
    untitledTitle,
  ]);

  const onDocumentSelect = useCallback(
    (anchor: ObjectWorkspaceAnchor) => {
      if (sessionId) {
        setOpenArtifact({ type: 'document', sessionId, documentId: anchor.id });
        return;
      }
      const document = patientDocuments.data?.find((item) => item.id === anchor.id);
      if (document && patientProfileId)
        setDocumentPreview({ patientProfileId, document });
    },
    [patientDocuments.data, patientProfileId, sessionId, setOpenArtifact]
  );
  const closeDocumentPreview = useCallback(() => setDocumentPreview(null), []);

  // session_gist duplicates session_name (the visit title); leave visit summary empty.
  const summary = sessionId ? undefined : recap?.recap?.blurb?.trim() || undefined;

  return {
    tasksState,
    documentsState,
    summary,
    onDocumentSelect,
    documentPreview,
    closeDocumentPreview,
  };
}
